import { useId, useState } from 'react'

import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { PasswordField } from '../../atoms/PasswordField.tsx'
import { passwordViolations } from '../../atoms/passwordPolicy.ts'
import { PasswordInput } from '../../atoms/PasswordInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'

/**
 * 비밀번호 변경 창 — 「내 계정」의 「비밀번호 · 변경」이 연다(eric 2026-09-23 「변경 누르면 비밀번호
 * 변경하는 창 뜨게 해줘」).
 *
 * 🔴 종전에는 세 칸이 구획에 펼쳐져 있고 「저장」은 **핸들러가 없는 죽은 단추**였다. 이 창이 그 셋을
 *    받고 `POST /users/{id}/password`(본인 재설정 · 현재 비밀번호 필수)로 실제로 보낸다.
 * 🔴 규칙 문장은 상시 표시하지 않는다(§1-1). 「변경」을 누를 때 재고 빠진 · 틀린 칸 아래에만 선다
 *    (eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」). 단추는 전송 중에만 막힌다.
 * 🔴 서버 사유(422 `WEAK_PASSWORD` · `USER_VALIDATION_FAIL` = 현재 비밀번호 틀림)는 **창 안**에 선다.
 *    창을 닫아 버리면 사람이 무엇이 틀렸는지 못 본다.
 */
export interface PasswordChangeModalProps {
  onClose: () => void
  /** 현재 · 새 비밀번호. 확인 칸은 화면이 맞춰 보고 넘기지 않는다 */
  onSubmit: (currentPassword: string, nextPassword: string) => void
  pending?: boolean
  /** 서버가 거절한 사유(`errorMessage()`). 없으면 `undefined` */
  error?: string | undefined
}
type PasswordChangeDraft = {
  currentPassword: string
  nextPassword: string
  confirmPassword: string
}

/** 누를 때 재는 규칙 — 현재 비밀번호 · 새 비밀번호(정책) · 확인 일치. 서버 거절(`WEAK_PASSWORD` 등)은 창 아래 `error` */
function validatePasswordChange(d: PasswordChangeDraft) {
  const violations = passwordViolations(d.nextPassword)
  return {
    current: d.currentPassword === '' ? t('validation.password.currentRequired') : undefined,
    next:
      d.nextPassword === ''
        ? t('validation.password.newRequired')
        : violations.length > 0
          ? violations.join(' · ')
          : undefined,
    confirm:
      d.confirmPassword === ''
        ? t('validation.password.confirmRequired')
        : d.confirmPassword !== d.nextPassword
          ? t('validation.password.mismatch')
          : undefined,
  }
}

export function PasswordChangeModal({
  onClose,
  onSubmit,
  pending = false,
  error,
}: PasswordChangeModalProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [nextPassword, setNextPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const errorId = useId()
  const form = useSubmitValidation(validatePasswordChange)
  const current = form.field('current')
  const next = form.field('next')
  const confirm = form.field('confirm')
  const draft: PasswordChangeDraft = { currentPassword, nextPassword, confirmPassword }

  const gate = pending
    ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
    : ({ disabled: false } as const)

  return (
    <Modal
      size="sm"
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (pending) return
              // 바닥 줄은 본문 밖이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
              if (form.validate(draft, document.body)) onSubmit(currentPassword, nextPassword)
            }}
            size="pill"
            variant="primary"
            {...gate}
          >
            {t('action.change')}
          </Button>
        </>
      }
      onClose={onClose}
      title={t('screen.passwordChange.title')}
    >
      <div className="pwgrid">
        <Field label={t('field.currentPassword')} {...current.fieldProps}>
          <PasswordInput
            {...current.inputProps}
            autoComplete="current-password"
            onChange={(value) => {
              setCurrentPassword(value)
              form.change('current', { ...draft, currentPassword: value })
            }}
            value={currentPassword}
            wide
          />
        </Field>
        <PasswordField
          error={next.fieldProps.error}
          id={next.inputProps.id}
          label={t('field.newPassword')}
          onChange={(value) => {
            setNextPassword(value)
            form.change('next', { ...draft, nextPassword: value }, ['confirm'])
          }}
          validation="submit"
          value={nextPassword}
        />
        <Field label={t('field.newPasswordConfirm')} {...confirm.fieldProps}>
          <PasswordInput
            {...confirm.inputProps}
            autoComplete="new-password"
            onChange={(value) => {
              setConfirmPassword(value)
              form.change('confirm', { ...draft, confirmPassword: value })
            }}
            value={confirmPassword}
            wide
          />
        </Field>
      </div>
      {error === undefined ? null : (
        <p className="a-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </Modal>
  )
}
